"use client";

import { useState, useRef, useCallback } from "react";
import type { DialogNode, DialogTopic, TopicVocabulary } from "@/lib/dialog-data";
import { getCachedResponse } from "@/lib/llm-cache";
import api, { type LocaleCode, type KnownLanguageCode, type Difficulty, type Richness } from "@/lib/api/client";
import type { StageName } from "@/data/loading-stages";

export interface StreamingMetadata {
  speaker?: string;
  title?: string;
  description?: string;
}

export interface GenerationProgress {
  stage: number;
  total: number;
  name: StageName;
  detail?: string;
}

export interface GenerationStats {
  startTime: number | null;
  endTime: number | null;
}

export interface CostBreakdown {
  endpoint: string;
  llm_calls: number;
  llm_cached: number;
  llm_prompt_tokens: number;
  llm_completion_tokens: number;
  llm_cost_usd: number;
  tts_calls: number;
  tts_cached: number;
  tts_characters: number;
  tts_cost_usd: number;
  img_calls: number;
  img_cached: number;
  img_cost_usd: number;
  stt_calls: number;
  stt_duration_seconds: number;
  stt_cost_usd: number;
  total_cost_usd: number;
}

export interface GenerationParams {
  topic: string;
  nativeLanguage: LocaleCode;
  targetLanguage: LocaleCode;
  targetLocale: LocaleCode;
  difficulty: Difficulty;
  richness: Richness;
  userName?: string;
  userGender?: string;
  knownLanguages?: KnownLanguageCode[];
  analyticsOptOut?: boolean;
  ttsSpeed?: number;
  skipCache?: boolean;
  cacheRequest: {
    topic: string;
    nativeLanguage: LocaleCode;
    targetLanguage: LocaleCode;
    difficulty: Difficulty;
  };
}

export interface AdaptParams {
  topicId: string;
  targetLanguage: LocaleCode;
  nativeLanguage: LocaleCode;
  knownLanguages?: KnownLanguageCode[];
  ttsSpeed?: number;
  userName?: string;
  userGender?: string;
}

export interface GenerationResult {
  topic: DialogTopic;
  fromCache: boolean;
  recovered: boolean;
}

export function useGenerationService() {
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generatedTopic, setGeneratedTopic] = useState<DialogTopic | null>(null);
  const [progress, setProgress] = useState<GenerationProgress | null>(null);
  const [intermediateVocabulary, setIntermediateVocabulary] = useState<TopicVocabulary | null>(null);
  const [streamingNodes, setStreamingNodes] = useState<(DialogNode | undefined)[]>([]);
  const [streamingMetadata, setStreamingMetadata] = useState<StreamingMetadata | null>(null);
  const [ttsReadyTexts, setTtsReadyTexts] = useState<string[]>([]);
  const [stats, setStats] = useState<GenerationStats>({
    startTime: null,
    endTime: null,
  });
  const [lastCost, setLastCost] = useState<CostBreakdown | null>(null);

  const currentJobIdRef = useRef<string | null>(null);
  const [flyMachineId, setFlyMachineId] = useState<string | null>(null);

  // Poll job status until complete (for recovery when SSE drops)
  const pollJobUntilComplete = async (jobId: string): Promise<DialogTopic | null> => {
    const maxAttempts = 120;
    const pollInterval = 1000;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const { response } = await api.GET("/api/jobs/{job_id}", {
          params: { path: { job_id: jobId } },
          parseAs: "stream",
        });
        if (!response.ok) {
          if (response.status === 404) return null;
          throw new Error("Failed to fetch job status");
        }

        const job = await response.json();

        if (job.progress) {
          setProgress({
            stage: job.progress.stage,
            total: job.progress.total,
            name: job.progress.name as StageName,
            detail: job.progress.detail,
          });
        }

        if (job.status === "completed" && job.result) {
          return job.result as DialogTopic;
        }

        if (job.status === "failed") {
          throw new Error(job.error || "Generation failed");
        }

        await new Promise((resolve) => setTimeout(resolve, pollInterval));
      } catch (err) {
        console.error("[PollJob] Error:", err);
        throw err;
      }
    }

    throw new Error("Job timed out");
  };

  // Shared SSE stream handler — processes response from /api/generate or /api/adapt-curriculum
  const handleSSEResponse = async (response: Response): Promise<GenerationResult | null> => {
    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error("No response body");
    }

    let completed = false;
    let result: GenerationResult | null = null;

    const handleComplete = (data: DialogTopic, fromPolling: boolean) => {
      if (completed) return;
      completed = true;
      currentJobIdRef.current = null;
      setProgress(null);
      const endTime = Date.now();
      setStats((prev) => ({ ...prev, endTime }));
      setGeneratedTopic(data);
      setIsGenerating(false);
      result = { topic: data, fromCache: false, recovered: fromPolling };
    };

    const decoder = new TextDecoder();
    let buffer = "";
    let currentEvent = "";

    const processLines = (lines: string[]) => {
      for (const line of lines) {
        if (line.startsWith("event: ")) {
          currentEvent = line.slice(7).trim();
        } else if (line.startsWith("data: ")) {
          const dataStr = line.slice(6);

          if (currentEvent === "error") {
            try {
              const data = JSON.parse(dataStr);
              throw new Error(data.error || data.message || "Generation failed");
            } catch (parseErr) {
              if (parseErr instanceof SyntaxError) {
                throw new Error(dataStr || "Generation failed");
              }
              throw parseErr;
            }
          }

          let data;
          try {
            data = JSON.parse(dataStr);
          } catch {
            console.warn("Failed to parse SSE data:", dataStr);
            continue;
          }

          if (currentEvent === "job") {
            currentJobIdRef.current = data.jobId;
            if (data.machineId) setFlyMachineId(data.machineId);
          } else if (currentEvent === "progress") {
            setProgress({
              stage: data.stage,
              total: data.total,
              name: data.name,
              detail: data.detail,
            });
          } else if (currentEvent === "metadata") {
            setStreamingMetadata(data);
          } else if (currentEvent === "node") {
            setStreamingNodes(prev => {
              const next = [...prev];
              next[data.index] = data.node;
              return next;
            });
          } else if (currentEvent === "vocalized_nodes") {
            // Merge vocalization tips (including alternatives) into existing streaming nodes
            if (data.nodes && Array.isArray(data.nodes)) {
              setStreamingNodes(prev => {
                const tipsById = new Map<string, { vocalizationTip: string; alternatives?: { text?: string; vocalizationTip?: string }[] }>();
                for (const tip of data.nodes) {
                  if (tip.id && tip.vocalizationTip) {
                    tipsById.set(tip.id, tip);
                  }
                }
                return prev.map(node => {
                  if (!node) return node;
                  const tipEntry = tipsById.get(node.id);
                  if (!tipEntry) return node;
                  const updated = { ...node, vocalizationTip: tipEntry.vocalizationTip };
                  if (tipEntry.alternatives && node.alternatives) {
                    updated.alternatives = node.alternatives.map((alt: any, i: number) => ({
                      ...alt,
                      vocalizationTip: tipEntry.alternatives?.[i]?.vocalizationTip ?? alt.vocalizationTip,
                    }));
                  }
                  return updated;
                });
              });
            }
          } else if (currentEvent === "tts_ready") {
            if (data.text) {
              setTtsReadyTexts(prev => [...prev, data.text]);
            }
          } else if (currentEvent === "vocabulary") {
            setIntermediateVocabulary(prev => {
              const base = prev ?? { words: [], expressions: [], topical: [] };
              // Merge words, dedup by baseForm
              const byBaseForm = new Map(base.words.map((w: any) => [w.baseForm, w]));
              for (const word of data.vocabulary.words ?? []) {
                byBaseForm.set(word.baseForm, word);
              }
              // Merge expressions, dedup by expression text
              const byExpr = new Map((base.expressions ?? []).map((e: any) => [e.expression, e]));
              for (const expr of data.vocabulary.expressions ?? []) {
                byExpr.set(expr.expression, expr);
              }
              // Merge topical, dedup by category name
              const byCat = new Map((base.topical ?? []).map((t: any) => [t.category, t]));
              for (const cat of data.vocabulary.topical ?? []) {
                byCat.set(cat.category, cat);
              }
              return {
                ...base,
                words: Array.from(byBaseForm.values()),
                expressions: Array.from(byExpr.values()),
                topical: Array.from(byCat.values()),
              };
            });
          } else if (currentEvent === "cost") {
            setLastCost(data as CostBreakdown);
          } else if (currentEvent === "complete") {
            setIntermediateVocabulary(null);
            setStreamingNodes([]);
            setStreamingMetadata(null);
            setTtsReadyTexts([]);
            handleComplete(data.data, false);
          }
        }
      }
    };

    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        buffer += decoder.decode();
        if (buffer.trim()) {
          processLines(buffer.split("\n"));
        }
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() || "";
      processLines(lines);
    }

    // Fallback: poll for result if SSE ended without complete event
    const jobId = currentJobIdRef.current;
    if (!completed && jobId) {
      console.log("[SSE] Stream ended without complete event, falling back to polling...");
      const recovered = await pollJobUntilComplete(jobId);
      if (recovered) {
        handleComplete(recovered, true);
      }
    }

    return result;
  };

  // Reset state before starting a new generation/adaptation
  const resetState = () => {
    setIsGenerating(true);
    setError(null);
    setGeneratedTopic(null);
    setProgress(null);
    setIntermediateVocabulary(null);
    setStreamingNodes([]);
    setStreamingMetadata(null);
    setTtsReadyTexts([]);
    setLastCost(null);
    setFlyMachineId(null);
    setStats({ startTime: Date.now(), endTime: null });
  };

  // Handle errors with job polling recovery
  const handleStreamError = async (err: unknown): Promise<GenerationResult | null> => {
    const jobId = currentJobIdRef.current;
    if (jobId) {
      console.log("[SSE] Error, attempting recovery via job polling...");
      try {
        const recovered = await pollJobUntilComplete(jobId);
        if (recovered) {
          currentJobIdRef.current = null;
          setProgress(null);
          setStats((prev) => ({ ...prev, endTime: Date.now() }));
          setGeneratedTopic(recovered);
          setIsGenerating(false);
          return { topic: recovered, fromCache: false, recovered: true };
        }
      } catch (recoveryErr) {
        console.error("[SSE] Recovery failed:", recoveryErr);
      }
    }

    setProgress(null);
    setStats((prev) => ({ ...prev, endTime: Date.now() }));
    setError(err instanceof Error ? err.message : "Failed to generate content");
    setIsGenerating(false);
    currentJobIdRef.current = null;
    return null;
  };

  const generate = useCallback(async (
    params: GenerationParams
  ): Promise<GenerationResult | null> => {
    resetState();

    // Check cache first
    if (!params.skipCache) {
      const cached = getCachedResponse(params.cacheRequest);
      if (cached) {
        setStats((prev) => ({
          ...prev,
          endTime: Date.now(),
        }));
        try {
          const jsonMatch = cached.match(/\{[\s\S]*\}/);
          if (jsonMatch) {
            const parsed = JSON.parse(jsonMatch[0]);
            setGeneratedTopic(parsed);
            setIsGenerating(false);
            return { topic: parsed, fromCache: true, recovered: false };
          }
        } catch {
          // Cache was invalid, continue with fresh generation
        }
      }
    }

    try {
      const { response } = await api.POST("/api/generate", {
        body: {
          topic: params.topic,
          nativeLanguage: params.nativeLanguage,
          targetLanguage: params.targetLanguage,
          targetLocale: params.targetLocale,
          difficulty: params.difficulty,
          richness: params.richness,
          userName: params.userName,
          userGender: params.userGender,
          knownLanguages: params.knownLanguages,
          analyticsOptOut: params.analyticsOptOut ?? false,
          ttsSpeed: params.ttsSpeed ?? 0.9,
        },
        parseAs: "stream",
      });

      if (!response.ok) {
        const text = await response.text();
        const dataMatch = text.match(/data:\s*(\{.*\})/);
        if (dataMatch) {
          try {
            const errorData = JSON.parse(dataMatch[1]!);
            throw new Error(errorData.error || "Failed to generate content");
          } catch (e) {
            if (!(e instanceof SyntaxError)) throw e;
          }
        }
        throw new Error(text || "Failed to generate content");
      }

      return await handleSSEResponse(response);
    } catch (err) {
      return await handleStreamError(err);
    }
  }, []);

  const adaptCurriculum = useCallback(async (
    params: AdaptParams
  ): Promise<GenerationResult | null> => {
    resetState();

    try {
      const { response } = await api.POST("/api/adapt-curriculum", {
        body: {
          topicId: params.topicId,
          targetLanguage: params.targetLanguage,
          nativeLanguage: params.nativeLanguage,
          knownLanguages: params.knownLanguages ?? [],
          ttsSpeed: params.ttsSpeed ?? 0.9,
          userName: params.userName,
          userGender: params.userGender,
        },
        parseAs: "stream",
      });

      if (!response.ok) {
        const text = await response.text();
        const dataMatch = text.match(/data:\s*(\{.*\})/);
        if (dataMatch) {
          try {
            const errorData = JSON.parse(dataMatch[1]!);
            throw new Error(errorData.error || "Failed to adapt curriculum");
          } catch (e) {
            if (!(e instanceof SyntaxError)) throw e;
          }
        }
        throw new Error(text || "Failed to adapt curriculum");
      }

      return await handleSSEResponse(response);
    } catch (err) {
      return await handleStreamError(err);
    }
  }, []);

  const cancel = useCallback(() => {
    setIsGenerating(false);
    setStreamingNodes([]);
    setStreamingMetadata(null);
    currentJobIdRef.current = null;
  }, []);

  return {
    generate,
    adaptCurriculum,
    cancel,
    isGenerating,
    error,
    setError,
    generatedTopic,
    progress,
    stats,
    intermediateVocabulary,
    streamingNodes,
    streamingMetadata,
    ttsReadyTexts,
    lastCost,
    flyMachineId,
  };
}
